import React, { useEffect, useMemo, useState } from 'react';
import { objectFileName, uploadFile } from '../../../../api/files';
import { useAuth } from '../../../../context/AuthContext';
import { getService, listServices } from '../../../../api/companyRecords';
import '../packages-v2.css';
import './package-editor-page.css';

function useEscapeToClose(open, onClose) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
}

const normalize = (v) => String(v ?? '').trim();
const money = (v) => Number(v || 0).toFixed(2);

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function PackageEditorPage({ open, serviceId, packageItem, busy, onClose, onSave }) {
  const { companyData } = useAuth();
  useEscapeToClose(open, onClose);

  const isEdit = !!packageItem?.id;
  const companyId = companyData?.id;

  const [uploading, setUploading] = useState(false);
  const isBusy = !!busy || uploading;

  const [addons, setAddons] = useState([]);
  const [baseServiceDuration, setBaseServiceDuration] = useState(0);

  // Keep a separate "baseServicePrice" for UX clarity (excluding add-ons).
  const [form, setForm] = useState({
    name: '',
    baseServicePrice: 0,
    discount: 0,
    isDiscountApplied: false,
    description: '',
    terms: '',
    imageUrl: '',
    duration: 0,
    bufferTime: 0,
    selectedAddons: [],
  });

  // Load service duration + all add-ons across services (like previous PackageForm).
  useEffect(() => {
    if (!open) return;
    if (!companyId || !serviceId) return;

    let cancelled = false;
    const run = async () => {
      try {
        const serviceSnap = await getService({ companyId, id: serviceId });
        if (cancelled) return;

        if (serviceSnap) {
          const dur = Number(serviceSnap.duration || 0);
          setBaseServiceDuration(dur);
          setForm((prev) => (isEdit ? prev : { ...prev, duration: dur }));
        }

        const servicesSnapshot = await listServices({ companyId });
        if (cancelled) return;

        let allAddons = [];
        servicesSnapshot.forEach((s) => {
          if (s?.addOns && Array.isArray(s.addOns) && s.addOns.length > 0) {
            const withService = s.addOns.map((a) => ({
              ...a,
              serviceName: s.name || 'Service',
            }));
            allAddons = [...allAddons, ...withService];
          }
        });

        const unique = Array.from(new Map(allAddons.map((a) => [a.id, a])).values());
        setAddons(unique);
      } catch (e) {
        console.error('Error fetching services/add-ons:', e);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, serviceId, isEdit]);

  // Initialize form when opening / switching package.
  useEffect(() => {
    if (!open) return;

    const p = packageItem || {};
    const selected = Array.isArray(p.selectedAddons) ? p.selectedAddons : [];
    const addonsTotal = selected.reduce((t, a) => t + safeNum(a.price), 0);
    const storedBase = safeNum(p.basePrice ?? p.price ?? 0);
    const computedBaseService = Math.max(0, storedBase - addonsTotal);

    setUploading(false);
    setForm({
      name: p.name || '',
      baseServicePrice: isEdit ? computedBaseService : 0,
      discount: safeNum(p.discount || 0),
      isDiscountApplied: !!p.isDiscountApplied,
      description: p.description || '',
      terms: p.terms || '',
      imageUrl: p.imageUrl || '',
      selectedAddons: selected.map((a) => ({
        id: a.id,
        name: a.name,
        duration: safeNum(a.duration || 0),
        bufferTime: safeNum(a.bufferTime || 0),
        price: safeNum(a.price || 0),
        serviceName: a.serviceName,
      })),
      duration: safeNum(p.duration || 0),
      bufferTime: safeNum(p.bufferTime || 0),
    });
  }, [open, packageItem, isEdit]);

  const addonsTotalPrice = useMemo(
    () => (form.selectedAddons || []).reduce((t, a) => t + safeNum(a.price), 0),
    [form.selectedAddons]
  );

  const totalBasePrice = useMemo(() => {
    const base = Math.max(0, safeNum(form.baseServicePrice));
    return Math.max(0, base + addonsTotalPrice);
  }, [form.baseServicePrice, addonsTotalPrice]);

  const currentPrice = useMemo(() => {
    const discount = Math.min(100, Math.max(0, safeNum(form.discount)));
    if (!form.isDiscountApplied || discount <= 0) return totalBasePrice;
    return Number((totalBasePrice * (1 - discount / 100)).toFixed(2));
  }, [totalBasePrice, form.discount, form.isDiscountApplied]);

  const totalDuration = useMemo(() => {
    const addonsMins = (form.selectedAddons || []).reduce((t, a) => t + safeNum(a.duration), 0);
    const base = safeNum(form.duration || baseServiceDuration);
    const buffer = safeNum(form.bufferTime);
    return Math.max(0, base + addonsMins + buffer);
  }, [form.selectedAddons, form.duration, form.bufferTime, baseServiceDuration]);

  const [addonSearch, setAddonSearch] = useState('');

  const groupedAddons = useMemo(() => {
    const q = normalize(addonSearch).toLowerCase();
    const filtered = q
      ? addons.filter((a) => `${a?.name || ''} ${a?.serviceName || ''}`.toLowerCase().includes(q))
      : addons;

    const groups = new Map();
    for (const a of filtered) {
      const key = a?.serviceName || 'Service';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(a);
    }

    const keys = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));
    return keys.map((k) => ({
      serviceName: k,
      items: groups
        .get(k)
        .slice()
        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))),
    }));
  }, [addons, addonSearch]);

  const selectedIds = useMemo(() => new Set((form.selectedAddons || []).map((a) => a.id)), [form.selectedAddons]);

  const toggleAddon = (addon) => {
    if (isBusy) return;
    if (!addon?.id) return;

    setForm((prev) => {
      const exists = (prev.selectedAddons || []).some((a) => a.id === addon.id);
      if (exists) {
        return { ...prev, selectedAddons: prev.selectedAddons.filter((a) => a.id !== addon.id) };
      }

      const next = {
        id: addon.id,
        name: addon.name,
        duration: safeNum(addon.duration || 0),
        bufferTime: safeNum(addon.bufferTime || 0),
        price: safeNum(addon.price || 0),
        serviceName: addon.serviceName,
      };
      return { ...prev, selectedAddons: [...(prev.selectedAddons || []), next] };
    });
  };

  const removeSelectedAddon = (id) => {
    setForm((prev) => ({ ...prev, selectedAddons: (prev.selectedAddons || []).filter((a) => a.id !== id) }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const basePath = companyId ? `companies/${companyId}/packages/${serviceId}` : `packages/${serviceId}`;
      const stored = await uploadFile(file, `${basePath}/${objectFileName(file)}`);
      setForm((prev) => ({ ...prev, imageUrl: stored.url }));
    } catch (error) {
      console.error('Error uploading package image:', error);
    } finally {
      setUploading(false);
    }
  };

  const canSubmit = useMemo(() => {
    const nameOk = normalize(form.name).length > 0;
    const durationOk = safeNum(form.duration) > 0;
    return nameOk && durationOk && !isBusy;
  }, [form.name, form.duration, isBusy]);

  const submit = () => {
    const name = normalize(form.name);
    if (!name) return;
    if (safeNum(form.duration) <= 0) return;

    const discount = Math.min(100, Math.max(0, safeNum(form.discount)));
    const payload = {
      id: packageItem?.id,
      name,
      searchName: name.toLowerCase(),
      description: normalize(form.description),
      terms: normalize(form.terms),
      imageUrl: form.imageUrl || '',
      serviceId,
      duration: Math.max(0, parseInt(String(form.duration || 0), 10)),
      bufferTime: Math.max(0, parseInt(String(form.bufferTime || 0), 10)),
      totalDuration,
      basePrice: Number(totalBasePrice),
      currentPrice: Number(currentPrice),
      discount: Number(discount),
      isDiscountApplied: !!form.isDiscountApplied,
      createdAt: packageItem?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      selectedAddons: (form.selectedAddons || []).map((a) => ({
        id: a.id,
        name: a.name,
        duration: Number(safeNum(a.duration)),
        bufferTime: Number(safeNum(a.bufferTime)),
        price: Number(safeNum(a.price)),
      })),
    };

    onSave?.(payload);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="pkg2-page" role="region" aria-label={isEdit ? 'Edit package' : 'New package'}>
      <div className="pkg2-pageHeader">
        <div style={{ minWidth: 0 }}>
          <div className="pkg2-pageTitle">{isEdit ? 'Edit Package' : 'New Package'}</div>
          <div className="pkg2-pageSub">Build a bundle of services and add-ons with optional discounts.</div>
        </div>
      </div>

      <div className="pkg2-pageBody">
        <div className="pkgp-formGrid">
          <section className="pkgp-panel" aria-label="Image">
            <div className="pkgp-field">
              <div className="pkgp-label">Image</div>
              <label className="pkgp-dropzone">
                <input
                  type="file"
                  accept="image/*"
                  disabled={isBusy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    handleImageUpload(file);
                    e.target.value = '';
                  }}
                />
                {form.imageUrl ? (
                  <img className="pkgp-dropzoneImg" src={form.imageUrl} alt="" />
                ) : (
                  <div className="pkgp-dropzoneEmpty">
                    <div className="pkgp-dropzoneIcon">📦</div>
                    <div style={{ fontWeight: 900 }}>{uploading ? 'Uploading…' : 'Upload image'}</div>
                    <div className="pkgp-hint">Optional</div>
                  </div>
                )}
              </label>
            </div>
          </section>

          <section className="pkgp-panel" aria-label="Details">
            <div className="pkgp-field">
              <label className="pkgp-label" htmlFor="pkgp-name">
                Package name
              </label>
              <input
                id="pkgp-name"
                className="pkgp-input2"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Haircut + Beard"
                autoFocus
                disabled={isBusy}
              />
            </div>

            <div className="pkgp-field">
              <div className="pkgp-label">Add-ons</div>
              <div className="pkgp-addonPicker" aria-label="Add-on picker">
                <div className="pkgp-addonPickerHeader">
                  <input
                    className="pkgp-input2"
                    style={{ height: 36 }}
                    value={addonSearch}
                    onChange={(e) => setAddonSearch(e.target.value)}
                    placeholder="Search add-ons…"
                    disabled={isBusy}
                  />
                </div>
                <div className="pkgp-addonList">
                  {groupedAddons.length === 0 ? (
                    <div className="pkgp-hint">No add-ons found.</div>
                  ) : (
                    groupedAddons.map((g) => (
                      <div key={g.serviceName}>
                        <div className="pkgp-addonGroupTitle">{g.serviceName}</div>
                        {g.items.map((a) => {
                          const checked = selectedIds.has(a.id);
                          return (
                            <label key={a.id} className="pkgp-addonItem">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleAddon(a)}
                                disabled={isBusy}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div className="pkgp-addonName">{a.name}</div>
                                <div className="pkgp-addonMeta">
                                  ${money(a.price)} • {safeNum(a.duration)}m
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {form.selectedAddons?.length ? (
                <div style={{ marginTop: 10 }}>
                  <div className="pkgp-hint" style={{ marginBottom: 8 }}>
                    Selected:
                  </div>
                  <div className="pkgp-chipRow">
                    {form.selectedAddons.map((a) => (
                      <span className="pkgp-chip" key={a.id} title={a.serviceName ? `From: ${a.serviceName}` : ''}>
                        {a.name} (${money(a.price)} • {safeNum(a.duration)}m)
                        <button type="button" aria-label={`Remove ${a.name}`} onClick={() => removeSelectedAddon(a.id)}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="pkgp-summary" aria-label="Summary">
              <div className="pkgp-summaryLine">Total duration: {totalDuration} minutes</div>
              <div className="pkgp-summaryMeta">
                Add-ons: ${money(addonsTotalPrice)} • Total before discount: ${money(totalBasePrice)} • Current: $
                {money(currentPrice)}
              </div>
            </div>

            <div className="pkgp-row2" style={{ marginTop: 12 }}>
              <div className="pkgp-field">
                <label className="pkgp-label" htmlFor="pkgp-duration">
                  Package duration (minutes)
                </label>
                <input
                  id="pkgp-duration"
                  className="pkgp-input2"
                  type="number"
                  min={0}
                  step={1}
                  value={form.duration}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setForm((p) => ({ ...p, duration: e.target.value }))}
                  disabled={isBusy}
                />
                <div className="pkgp-hint">Base service duration: {baseServiceDuration} min</div>
              </div>

              <div className="pkgp-field">
                <label className="pkgp-label" htmlFor="pkgp-buffer">
                  Buffer time (minutes)
                </label>
                <input
                  id="pkgp-buffer"
                  className="pkgp-input2"
                  type="number"
                  min={0}
                  step={1}
                  value={form.bufferTime}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setForm((p) => ({ ...p, bufferTime: e.target.value }))}
                  disabled={isBusy}
                />
                <div className="pkgp-hint">Optional</div>
              </div>
            </div>

            <div className="pkgp-field" style={{ marginTop: 4 }}>
              <div className="pkgp-label">Pricing</div>
              <div className="pkgp-row2">
                <div className="pkgp-field" style={{ marginBottom: 0 }}>
                  <label className="pkgp-hint" htmlFor="pkgp-baseServicePrice">
                    Base price (without add-ons)
                  </label>
                  <input
                    id="pkgp-baseServicePrice"
                    className="pkgp-input2"
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.baseServicePrice}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setForm((p) => ({ ...p, baseServicePrice: e.target.value }))}
                    disabled={isBusy}
                  />
                </div>

                <div className="pkgp-field" style={{ marginBottom: 0 }}>
                  <label className="pkgp-hint" htmlFor="pkgp-discount">
                    Discount (%)
                  </label>
                  <input
                    id="pkgp-discount"
                    className="pkgp-input2"
                    type="number"
                    min={0}
                    max={100}
                    step="1"
                    value={form.discount}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setForm((p) => ({ ...p, discount: e.target.value }))}
                    disabled={isBusy}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="svc-btn svc-btn--info svc-btn--sm"
                  onClick={() => setForm((p) => ({ ...p, isDiscountApplied: !p.isDiscountApplied }))}
                  disabled={isBusy || safeNum(form.discount) <= 0}
                  style={{ whiteSpace: 'nowrap', minWidth: 160 }}
                >
                  {form.isDiscountApplied ? 'Remove Discount' : 'Apply Discount'}
                </button>
                <div className="pkgp-hint">
                  Current price: <span style={{ fontWeight: 900 }}>${money(currentPrice)}</span>
                  {form.isDiscountApplied && safeNum(form.discount) > 0 ? ` (${safeNum(form.discount)}% off)` : ''}
                </div>
              </div>
            </div>

            <div className="pkgp-field">
              <label className="pkgp-label" htmlFor="pkgp-description">
                Description
              </label>
              <textarea
                id="pkgp-description"
                className="pkgp-textarea2"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional"
                disabled={isBusy}
              />
            </div>

            <div className="pkgp-field" style={{ marginBottom: 0 }}>
              <label className="pkgp-label" htmlFor="pkgp-terms">
                Package terms
              </label>
              <textarea
                id="pkgp-terms"
                className="pkgp-textarea2"
                value={form.terms}
                onChange={(e) => setForm((p) => ({ ...p, terms: e.target.value }))}
                placeholder="Optional"
                disabled={isBusy}
              />
            </div>
          </section>
        </div>
      </div>

      <div className="pkg2-pageFooter">
        <button className="svc-btn svc-btn--ghost" type="button" onClick={onClose} disabled={isBusy}>
          Cancel
        </button>
        <button className="svc-btn svc-btn--primary" type="button" onClick={submit} disabled={!canSubmit}>
          {isBusy ? <span className="svc-spinner" /> : isEdit ? 'Save changes' : 'Create package'}
        </button>
      </div>
    </div>
  );
}

